import { useForm, yupResolver } from "@mantine/form";
import React, { FC, memo } from "react";
import { forms } from "../../../../forms";
import {
  Button,
  FileInput,
  Grid,
  Image,
  Modal,
  Textarea,
  TextInput,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import CropPickerInput from "../../../../components/ImageCropPicker/CropPickerInput";
import { useAddProjectMutation } from "../../../../hooks/projects/mutation/useAddProject.mutation";
import { useUpdateProjectMutation } from "../../../../hooks/projects/mutation/useUpdateProject.mutation";

interface IProjectModal {
  show: boolean;
  onClose: () => void;
  refetch: () => void;
  data?: TProjects;
}

const ProjectModal: FC<IProjectModal> = ({ data, onClose, show, refetch }) => {
  const formHandler = useForm({
    initialValues: {
      ...forms.projects.values,
      ...data,
    },
    validate: yupResolver(forms.projects.validation),
    validateInputOnBlur: true,
    validateInputOnChange: true,
  });

  const { onSubmit, getInputProps } = formHandler;

  const { mutateAsync: createService, isPending: createLoading } =
    useAddProjectMutation();
  const { mutateAsync: updateService, isPending: updateLoading } =
    useUpdateProjectMutation();

  const handleSubmit = async (values: typeof forms.projects.values) => {
    const formData = new FormData();
    if (typeof values.banner !== "string") {
      formData.append("banner", values.banner, "banner.webp");
    }
    formData.append("title", values.title);
    formData.append("description", values.description);
    formData.append("_id", values._id);
    const res = data
      ? await updateService(formData)
      : await createService(formData);
    if (res.status === "success") {
      onClose();
      refetch();
      showNotification({ message: res.message, color: "green" });
    } else {
      showNotification({ message: res.data.message, color: "red" });
    }
  };

  return (
    <Modal
      opened={show}
      onClose={onClose}
      title={data ? "Edit Project" : "Add Project"}
      size={"md"}
    >
      <form onSubmit={onSubmit(handleSubmit)}>
        <Grid gutter={10}>
          <Grid.Col lg={12}>
            <TextInput {...getInputProps("title")} label="Title" />
          </Grid.Col>
          <Grid.Col lg={12}>
            <Textarea
              autosize
              minRows={5}
              {...getInputProps("description")}
              label="Description"
            />
          </Grid.Col>

          <Grid.Col lg={12}>
            <CropPickerInput
              name="banner"
              label="Banner (ratio: 500 × 500)"
              formHandler={formHandler}
              pickerRatio={500 / 500}
            />
            {formHandler.values?.banner && (
              <Image
                src={
                  typeof formHandler.values?.banner === "string"
                    ? formHandler.values?.banner
                    : URL.createObjectURL(formHandler.values?.banner)
                }
                my={10}
              />
            )}
          </Grid.Col>
          <Grid.Col lg={12}>
            <Button
              loading={createLoading || updateLoading}
              disabled={createLoading || updateLoading}
              type="submit"
              fullWidth
              variant="filled"
              color="cyan"
            >
              Submit
            </Button>
          </Grid.Col>
        </Grid>
      </form>
    </Modal>
  );
};

export default memo(ProjectModal);
